// Org-wide Missing inbox over mfg_recipe_pending_lines.

import { useCallback, useEffect, useState } from "react";
import {
  createMaterial,
  listManufacturingAllPendingLines,
  reconcileManufacturingRecipePendingLine,
} from "@/lib/manufacturingClient";
import type { MfgRecipePendingLine } from "@/lib/manufacturing/types";
import type {
  CreateMaterialFromPendingInput,
  ReconcileResult,
} from "./useManufacturingPendingLines";

export function useManufacturingInventoryMissing(opts: {
  enabled: boolean;
  onChanged?: () => void;
}) {
  const { enabled, onChanged } = opts;
  const [rows, setRows] = useState<MfgRecipePendingLine[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!enabled) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);
    const r = await listManufacturingAllPendingLines();
    setLoading(false);
    if (!r.ok) {
      setError(r.error || "Failed to load missing materials");
      setRows([]);
      setTotal(0);
      return;
    }
    setRows(r.data.pending_lines);
    setTotal(r.data.total);
  }, [enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const matchExisting = useCallback(
    async (pendingLineId: string, materialId: string): Promise<ReconcileResult> => {
      setBusyId(pendingLineId);
      const r = await reconcileManufacturingRecipePendingLine({
        pending_line_id: pendingLineId,
        action: "match",
        material_id: materialId,
      });
      setBusyId(null);
      if (!r.ok) return { ok: false, error: r.error || "Reconcile failed" };
      await reload();
      onChanged?.();
      return { ok: true };
    },
    [onChanged, reload]
  );

  const discard = useCallback(
    async (pendingLineId: string): Promise<ReconcileResult> => {
      setBusyId(pendingLineId);
      const r = await reconcileManufacturingRecipePendingLine({
        pending_line_id: pendingLineId,
        action: "discard",
      });
      setBusyId(null);
      if (!r.ok) return { ok: false, error: r.error || "Discard failed" };
      await reload();
      onChanged?.();
      return { ok: true };
    },
    [onChanged, reload]
  );

  const createAndMatch = useCallback(
    async (
      pendingLineId: string,
      input: CreateMaterialFromPendingInput
    ): Promise<ReconcileResult> => {
      setBusyId(pendingLineId);
      const created = await createMaterial({
        name: input.name,
        category: input.category,
        unit: input.unit,
      });
      if (!created.ok) {
        setBusyId(null);
        return { ok: false, error: created.error || "Material create failed" };
      }
      const r = await reconcileManufacturingRecipePendingLine({
        pending_line_id: pendingLineId,
        action: "match",
        material_id: created.data.material.id,
      });
      setBusyId(null);
      if (!r.ok) return { ok: false, error: r.error || "Reconcile failed" };
      await reload();
      onChanged?.();
      return { ok: true };
    },
    [onChanged, reload]
  );

  return {
    rows,
    total,
    loading,
    error,
    busyId,
    reload,
    matchExisting,
    discard,
    createAndMatch,
  };
}
